import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldCheck, ShieldOff, Copy, AlertTriangle, Loader2, X } from 'lucide-react';
import api from '../lib/api';

/**
 * MFA Settings Modal — Enable/Disable 2FA from user profile
 * Shows QR code for setup, recovery codes, and toggle to disable
 */
const MfaSettingsModal = ({ isOpen, onClose, darkMode = false }) => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('status'); // status | setup | verify | recovery | disable
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/mfa/status');
      setMfaEnabled(res.data.mfaEnabled);
      setStep('status');
    } catch (err) {
      console.error('MFA status error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      setError('');
      setTotpCode('');
      setDisableCode('');
    }
  }, [isOpen, fetchStatus]);

  const handleSetup = async () => {
    try {
      setError('');
      setLoading(true);
      const res = await api.post('/api/mfa/setup');
      setSecret(res.data.secret);
      setOtpauthUri(res.data.otpauthUri);
      setStep('setup');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    try {
      setError('');
      setLoading(true);
      const res = await api.post('/api/mfa/verify-setup', { code: totpCode });
      setRecoveryCodes(res.data.recoveryCodes || []);
      setMfaEnabled(true);
      setStep('recovery');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) {
      setError('Enter the 6-digit code to disable MFA');
      return;
    }
    try {
      setError('');
      setLoading(true);
      await api.post('/api/mfa/disable', { code: disableCode });
      setMfaEnabled(false);
      setStep('status');
      setDisableCode('');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Cannot disable MFA.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  if (!isOpen) return null;

  const bg = darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900';
  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
  const inputBg = darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className={`${bg} rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Shield className="text-blue-500" size={24} />
            <h2 className="text-lg font-bold">Security Settings</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {loading && step === 'status' ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-blue-500" size={32} />
            </div>
          ) : (
            <>
              {/* ═══ STATUS VIEW ═══ */}
              {step === 'status' && (
                <div className="space-y-4">
                  <div className={`flex items-center justify-between p-4 rounded-xl border ${cardBg}`}>
                    <div className="flex items-center gap-3">
                      {mfaEnabled ? (
                        <ShieldCheck className="text-green-500" size={28} />
                      ) : (
                        <ShieldOff className="text-orange-500" size={28} />
                      )}
                      <div>
                        <p className="font-semibold">Two-Factor Authentication</p>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {mfaEnabled ? 'Enabled — your account is protected' : 'Disabled — add extra security'}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${mfaEnabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`}>
                      {mfaEnabled ? 'ON' : 'OFF'}
                    </span>
                  </div>

                  {mfaEnabled ? (
                    <button
                      onClick={() => { setStep('disable'); setError(''); setDisableCode(''); }}
                      className="w-full py-3 px-4 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                    >
                      <ShieldOff size={18} />
                      Disable 2FA
                    </button>
                  ) : (
                    <button
                      onClick={handleSetup}
                      disabled={loading}
                      className="w-full py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <ShieldCheck size={18} />
                      {loading ? 'Setting up...' : 'Enable 2FA'}
                    </button>
                  )}
                </div>
              )}

              {/* ═══ SETUP VIEW — Show secret for authenticator ═══ */}
              {step === 'setup' && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border ${cardBg}`}>
                    <p className="font-semibold mb-2">Step 1: Add to your authenticator app</p>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Open Google Authenticator, Authy, or 1Password and scan or enter this code:
                    </p>

                    {/* Manual entry code */}
                    <div className="flex items-center gap-2">
                      <code className={`flex-1 p-3 rounded-lg font-mono text-sm tracking-wider break-all ${inputBg} border`}>
                        {secret}
                      </code>
                      <button
                        onClick={() => copyToClipboard(secret)}
                        className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                        title="Copy secret"
                      >
                        <Copy size={18} />
                      </button>
                    </div>

                    {/* QR code link */}
                    <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Or scan QR code: open your authenticator &rarr; Add account &rarr; Scan QR code
                    </p>
                    {otpauthUri && (
                      <div className="mt-2 p-3 bg-white rounded-lg flex items-center justify-center">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUri)}`}
                          alt="MFA QR Code"
                          className="w-48 h-48"
                        />
                      </div>
                    )}
                  </div>

                  <div className={`p-4 rounded-xl border ${cardBg}`}>
                    <p className="font-semibold mb-2">Step 2: Enter the 6-digit code</p>
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="000000"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className={`w-full p-3 rounded-lg border text-center text-2xl tracking-[0.5em] font-mono ${inputBg}`}
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                      <AlertTriangle size={16} /> {error}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => { setStep('status'); setError(''); }}
                      className={`flex-1 py-3 px-4 rounded-xl border font-semibold transition-colors ${darkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-100'}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleVerifySetup}
                      disabled={loading || totpCode.length !== 6}
                      className="flex-1 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Verifying...' : 'Activate 2FA'}
                    </button>
                  </div>
                </div>
              )}

              {/* ═══ RECOVERY CODES VIEW ═══ */}
              {step === 'recovery' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                    <ShieldCheck size={20} />
                    <span className="font-semibold">2FA is now active!</span>
                  </div>

                  <div className={`p-4 rounded-xl border ${cardBg}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="text-orange-500" size={20} />
                      <p className="font-semibold text-orange-600 dark:text-orange-400">Save your recovery codes!</p>
                    </div>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      If you lose access to your authenticator app, use one of these codes to log in. Each code works only once.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {recoveryCodes.map((code, i) => (
                        <code key={i} className={`p-2 rounded-lg text-center font-mono text-sm ${inputBg} border`}>
                          {code}
                        </code>
                      ))}
                    </div>
                    <button
                      onClick={() => copyToClipboard(recoveryCodes.join('\n'))}
                      className="mt-3 w-full py-2 px-4 rounded-lg border flex items-center justify-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium"
                    >
                      <Copy size={16} /> Copy all codes
                    </button>
                  </div>

                  <button
                    onClick={() => { onClose(); }}
                    className="w-full py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
                  >
                    Done — I saved my codes
                  </button>
                </div>
              )}

              {/* ═══ DISABLE VIEW ═══ */}
              {step === 'disable' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
                    <AlertTriangle size={20} />
                    <span className="font-semibold">This will remove 2FA protection</span>
                  </div>

                  <div className={`p-4 rounded-xl border ${cardBg}`}>
                    <p className="font-semibold mb-2">Enter your authenticator code to confirm</p>
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="000000"
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className={`w-full p-3 rounded-lg border text-center text-2xl tracking-[0.5em] font-mono ${inputBg}`}
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                      <AlertTriangle size={16} /> {error}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => { setStep('status'); setError(''); }}
                      className={`flex-1 py-3 px-4 rounded-xl border font-semibold transition-colors ${darkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-100'}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDisable}
                      disabled={loading || disableCode.length !== 6}
                      className="flex-1 py-3 px-4 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Disabling...' : 'Disable 2FA'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MfaSettingsModal;
